'use client'

import Heading from '../../../../shared/components/ui/typography/heading'
import DashboardContentWrapper from '../../../../shared/components/dashboard-content-wrapper'
import PaginationDataContextProvider from '../../../../features/homeowners/context/pagination-data-context'
import PlanningTabs from '../../../../features/homeowners/components/planning-tabs'
import FilterSearchTab from '../../../../features/homeowners/components/filter-search-tab'
import PlanningPriorityTab from '../../../../features/homeowners/components/planning-priority-tab'
import PlanningMapTab from '../../../../features/homeowners/components/planning-map-tab'
import PlanningAiChatTab from '../../../../features/homeowners/components/planning-ai-chat-tab'
import { planningTabLinkSchema } from '../../../../features/homeowners/lib/zod/schemas'
import { PlanningTabLink } from '../../../../features/homeowners/constants/planning-tab-links'
import { UrlSearchParams } from '../../../../shared/types'
import LocationSelector from '../../../../shared/components/location-selector'
import React, { useEffect, useState } from 'react'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'
import { useRouter, useSearchParams } from 'next/navigation'
import { useRegion } from '@/shared/hooks/use-region'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { buildRegionUrl } from '@/shared/lib/utils'
import { SEARCH_PARAMS_TAB_KEY } from '@/features/homeowners/constants/planning-tab-links'
import { getHomeownerProfile } from '@/features/profile/api/homeowner-profile.api'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/shared/components/ui/button'
import ScrollToTopFab from '@/shared/components/scroll-to-top-fab'
import { authFetchJson } from '@/shared/lib/auth-fetch'
import { HOME } from '@/shared/constants/backend-api'

function PlanningPage() {
    const searchParamsHook = useSearchParams()
    const [tab, setTab] = useState<PlanningTabLink | undefined>(undefined)
    const [homeId, setHomeId] = useState<string | undefined>(undefined)
    const { session, isLoading: isSessionLoading } = useSession()
    const router = useRouter()
    const queryClient = useQueryClient()
    const { regionSlug } = useRegion()
    const { region, regionId } = useRegionId()
    const canViewPriority = session?.role === UserRole.ADMIN || 
                          session?.role === UserRole.BASE_LEADER || 
                          session?.role === UserRole.TEAM_LEADER || 
                          session?.role === UserRole.VOLUNTEER
    const isHomeowner = session?.role === UserRole.HOMEOWNER

    // Redirect homeowners to their own home page
    useEffect(() => {
        const redirectHomeowner = async () => {
            if (isHomeowner && session?.sub) {
                try {
                    const homeownerRes = await getHomeownerProfile()
                    if (homeownerRes.success && homeownerRes.data?.id) {
                        // Use homeowner ID, not home ID, for the route
                        const homeownerId = homeownerRes.data.id
                        const redirectUrl = regionSlug
                            ? buildRegionUrl(regionSlug, `/homeowners/${homeownerId}`)
                            : `/homeowners/${homeownerId}`
                        router.replace(redirectUrl)
                    }
                } catch (error) {
                    console.error('Error fetching homeowner profile for redirect:', error)
                }
            }
        }

        redirectHomeowner()
    }, [isHomeowner, session?.sub, regionSlug, router])

    useEffect(() => {
        const parseParams = () => {
            // Skip parsing if user is a homeowner (they should be redirected)
            if (isHomeowner) {
                return
            }

            const tabParam = searchParamsHook.get('tab')
            const homeIdParam = searchParamsHook.get('homeId') || undefined
            
            // Only redirect based on role when session has loaded - otherwise we'd redirect
            // away from ai-chat/priority on page refresh before session is available
            if (!isSessionLoading) {
                if (tabParam === 'priority' && !canViewPriority) {
                    const redirectUrl = regionSlug
                        ? buildRegionUrl(regionSlug, '/homeowners', { [SEARCH_PARAMS_TAB_KEY]: 'filter-search' })
                        : `/homeowners?${SEARCH_PARAMS_TAB_KEY}=filter-search`
                    router.replace(redirectUrl)
                    setTab('filter-search')
                    setHomeId(homeIdParam)
                    return
                }
                const canViewAiChatParam = session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER
                if (tabParam === 'ai-chat' && !canViewAiChatParam) {
                    const redirectUrl = regionSlug
                        ? buildRegionUrl(regionSlug, '/homeowners', { [SEARCH_PARAMS_TAB_KEY]: 'filter-search' })
                        : `/homeowners?${SEARCH_PARAMS_TAB_KEY}=filter-search`
                    router.replace(redirectUrl)
                    setTab('filter-search')
                    setHomeId(homeIdParam)
                    return
                }
            }
            
            const parsedTab = planningTabLinkSchema.safeParse(tabParam)
            
            if (tabParam && !parsedTab.success) {
                // Always default to filter-search if no valid tab specified
                setTab('filter-search')
                return
            }
            
            // Always default to filter-search if no tab specified
            setTab(parsedTab.data as PlanningTabLink || 'filter-search')
            setHomeId(homeIdParam)
        }

        parseParams()
    }, [searchParamsHook, canViewPriority, regionSlug, router, isHomeowner, session?.role, isSessionLoading])

    // Prefetch map (lightweight homes) data when Homeowners section mounts so Map tab loads faster
    useEffect(() => {
        if (isHomeowner || regionId == null || regionId < 1) return
        queryClient.prefetchQuery({
            queryKey: ['homes', 'lightweight', regionId],
            queryFn: async () => {
                const url = `${HOME.base}/lightweight?regionId=${regionId}`
                const res = await authFetchJson<any>(url)
                if (!res.success) throw new Error(res.message || 'Failed to fetch lightweight homes')
                return {
                    success: true,
                    data: res.data
                }
            },
            staleTime: 5 * 60 * 1000,
        })
    }, [regionId, isHomeowner, queryClient])

    const canViewAiChat = session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER

    // Determine which tab content to show - default to filter-search
    const effectiveTab = tab === "priority" && canViewPriority
        ? "priority" 
        : tab === "filter-search" 
        ? "filter-search" 
        : tab === "map"
        ? "map"
        : tab === "ai-chat" && canViewAiChat
        ? "ai-chat"
        : "filter-search"

    // Show loading state while redirecting homeowners
    if (isHomeowner) {
        return (
            <DashboardContentWrapper className="flex flex-col gap-[15px] pb-24 md:pb-5">
                <div>Loading...</div>
            </DashboardContentWrapper>
        )
    }

    return (
        <DashboardContentWrapper className="flex flex-col gap-[15px] pb-24 md:pb-5">
            <PaginationDataContextProvider>
                <div className="flex flex-col gap-4 md:flex-row md:justify-between">
                    <Heading>Homeowners</Heading>
                    <div className="flex items-baseline gap-2 justify-end">
                        <span className="hidden print:block text-sm font-medium" aria-hidden>
                            {region?.name ?? regionSlug?.replace(/-/g, ' ') ?? ''}
                        </span>
                        <div className="print-hidden">
                            <LocationSelector />
                        </div>
                    </div>
                </div>
                <div className="print-hidden">
                    <PlanningTabs tab={tab} />
                </div>
                {effectiveTab === "priority" && <PlanningPriorityTab />}
                {effectiveTab === "filter-search" && <FilterSearchTab />}
                {effectiveTab === "map" && <PlanningMapTab homeId={homeId} />}
                {effectiveTab === "ai-chat" && <PlanningAiChatTab />}
                <ScrollToTopFab />
            </PaginationDataContextProvider>
        </DashboardContentWrapper>
    )
}

export default PlanningPage

